import React, { useState } from "react";
import { X } from "lucide-react";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import { useGlobalModal } from "@/components/GlobalModalProvider/GlobalModalProvider";
import { showApiErrorModal } from "@/utils/common/apiErrorHelper";
import "./CreateUserModal.css";

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type Role = "OWNER" | "OPERATOR";

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("OPERATOR");
  const [showPassword, setShowPassword] = useState(false);

  const { userData } = useUser();
  const modal = useGlobalModal();

  if (!isOpen) return null;

  const resetForm = () => {
    setName("");
    setEmail("");
    setPassword("");
    setRole("OPERATOR");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName || !trimmedEmail || !password) {
      modal?.show({
        type: "warning",
        title: "Campos obrigatórios",
        message: "Por favor, preencha todos os campos obrigatórios.",
        autoClose: 3000,
      });
      setLoading(false);
      return;
    }

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
    if (!passwordRegex.test(password)) {
      modal?.show({
        type: "warning",
        title: "Senha inválida",
        message:
          "A senha deve ter no mínimo 8 caracteres, incluindo uma letra maiúscula, uma minúscula, um número e um caractere especial (@$!%*?&#).",
        autoClose: 5000,
      });
      setLoading(false);
      return;
    }

    try {
      await api.post(
        "/user/create",
        { name: trimmedName, email: trimmedEmail, password, role },
        {
          headers: {
            Authorization: `Bearer ${userData?.auth}`,
            "Content-Type": "application/json",
          },
        }
      );

      modal?.show({
        type: "success",
        title: "Sucesso",
        message: `Usuário "${trimmedName}" criado com sucesso como ${role === "OWNER" ? "Proprietário" : "Operador"}.`,
        autoClose: 3000,
      });

      resetForm();
      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error("Erro ao criar usuário:", err);
      showApiErrorModal(modal, err, {
        title: "Erro ao criar usuario",
        fallbackMessage: "Erro ao criar usuario. Tente novamente.",
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-user-modal-overlay">
      <div className="create-user-modal-content">
        <div className="create-user-modal-header">
          <h2>Criar Novo Usuário</h2>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="create-user-form">
          <div className="form-group">
            <label htmlFor="name">Nome *</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome completo"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">E-mail *</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@exemplo.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Senha *</label>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "Ocultar" : "Mostrar"}
              </button>
            </div>
            <small className="password-hint">
              A senha deve conter: maiúscula, minúscula, número e caractere especial
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="role">Tipo de Usuário *</label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
            >
              <option value="OPERATOR">Operador</option>
              <option value="OWNER">Proprietário</option>
            </select>
            <small className="role-hint">
              {role === "OWNER"
                ? "Proprietários têm acesso total, incluindo Dashboard e criação de usuários."
                : "Operadores não têm acesso ao Dashboard principal."}
            </small>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? "Criando..." : "Criar Usuário"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;
